'use client';

import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/src/shared/ui/popover';
import { cn } from '@/src/shared/lib/shadcn';
import { useHoverablePopover } from '@/src/shared/lib/useHoverablePopover';

interface KpiHintProps {
  /** The caption under a figure, such as how many accounts it adds up. */
  label: string;
  /** Sentence saying what the caption and its figure stand for, shown on hover, tap or focus. */
  hint: string;
  className?: string;
}

/**
 * A caption that explains itself. The sibling of `KpiChange` for a line that
 * states a fact instead of a movement, so it carries no arrow and no colour.
 */
export const KpiHint = ({ label, hint, className }: KpiHintProps) => {
  const { open, onOpenChange, hoverProps, contentProps } = useHoverablePopover();

  return (
    // Popover rather than Tooltip because Radix tooltips never open on tap.
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        type="button"
        data-testid="kpi-hint"
        // The line is shorter than a comfortable touch target, so a transparent
        // pseudo-element extends the hit area without moving anything.
        className={cn(
          "relative flex w-fit items-center gap-1.5 rounded-sm text-left text-footnote text-muted-foreground outline-none before:absolute before:-inset-x-2 before:-inset-y-3.5 before:content-[''] hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50",
          className,
        )}
        {...hoverProps}
      >
        <span>{label}</span>
        <Info aria-hidden className="size-3.5 shrink-0" />
      </PopoverTrigger>
      <PopoverContent
        data-testid="kpi-hint-text"
        className="w-72 p-3 text-center text-xs"
        {...contentProps}
        {...hoverProps}
      >
        {hint}
      </PopoverContent>
    </Popover>
  );
};
